/**
 * CUSTOMER FORM WIZARD
 *
 * Multi-step wizard for creating new customers with spacious layout design.
 *
 * Features:
 * - Three-step wizard: Primary Information → Financial Information → Preference
 * - Generous white space and breathing room
 * - Gray background field grouping with proper padding
 * - Custom label styling (small, secondary color)
 * - Sticky bottom buttons (Exit + Previous/Next/Submit)
 * - Sticky side steps with fixed height
 * - Scrollable content area
 * - React Hook Form integration
 * - Contacts and locations management
 * - API integration for customer creation
 */

import React, { useState, useEffect, useCallback, useRef, useReducer, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDebounceFn, useDebounce } from 'ahooks';
import {
  Form,
  Input,
  Select,
  Button,
  Layout,
  theme,
  App,
  Badge,
  Typography,
  Row,
  Col,
  Alert,
  Table,
  Radio,
  Dropdown,
  Spin,
  Modal,
  Checkbox,
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import {
  type CustomerContact,
  type CustomerLocation,
  type HoldStatusType,
  HOLD_STATUS_OPTIONS,
} from '@/models/customer';
import LocationFormModal, { type LocationFormData } from '@/components/customers/LocationFormModal';
import CategorySelectWithCreate from '@/components/shared/CategorySelectWithCreate';
import TableContainer from '@/components/common/TableContainer';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';
import { useCascadingLocations } from '@/hooks/useLocations';
import { useTranslation } from 'react-i18next';
import {
  useCheckCustomerExists,
  useCustomerMutations,
  useCustomerDetail,
  useCustomerLocations,
  useCustomerContacts,
  useCustomerOptions,
  useCustomerLocationDetail,
} from '@/hooks/useCustomers';
import { useCustomerPayment, useCustomerPaymentMutations } from '@/hooks/useCustomerPayment';
import { usePaymentTerms } from '@/hooks/usePaymentTerms';
import { useTaxTypes } from '@/hooks/useTaxTypes';
import { useCurrencies } from '@/hooks/useCurrencies';
import { customerApi } from '@/services/api/customer.api';
import { locationApi } from '@/services/api/location.api';
import { useFormStyles } from '@/styles/formStyles';
import type { CategoryListItem } from '@/types/api/category';
import { holdStatusToNumber, numberToHoldStatus, type HoldStatus } from '@/types/api/customer';
import { getCategories } from '@/apis/endpoints/categories';
import { useNavigationGuard } from '@/contexts/NavigationGuardContext';
import { useUnsavedChangesModal } from '@/components/common/ConfirmModal';
import { DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import { formatAddressForDisplay, sanitizeLocationIds } from '@/utils/locationUtils';
import { parseEntityRouteId } from '@/utils/routeIdUtils';
import { useCustomerPermissions, getToggleStatusStyle } from '@/hooks/useCustomerPermissions';

const { Sider, Content } = Layout;
const { Text } = Typography;

interface FormValues {
  // Step 1: Primary Information
  name: string; // Changed from customerName to match API
  alternativeName?: string;
  nickname?: string;
  individual: boolean; // Changed from type: CustomerType to match API
  categoryIds: number[];
  country: string;
  stateId: number | null;
  state: string;
  cityId: number | null;
  city: string;
  address: string;
  postcode: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  fax?: string;
  email: string;
  localTaxId: string; // Changed from abn to match API
  web?: string;
  // Relationships
  internalContactUserId?: number; // 業務人員 (Sales Rep / Internal Contact)
  parentCompanyId?: number;
  // Notes
  accountNotes?: string;
  deliveryNotes?: string;
  salesNotes?: string;
  // Online account
  isActiveOnlineAccount?: boolean; // Changed from activeOnlineAccount
  // Collections
  contacts: CustomerContact[];
  locations: CustomerLocation[];

  // Step 2: Financial Information - Account Information
  deposit?: number; // Percentage
  cashDiscount?: number; // Percentage
  basePaymentTermId?: number; // Payment term ID from API
  dueInDays?: number;
  creditLimit?: number;
  hold: HoldStatusType;
  primaryCurrency: string; // Required by the payment API (F2.2)
  // Tax Information
  taxTypeId?: number; // Tax type ID from API

  // Step 3: Preference (TODO: These moved to CustomerPaymentResponse)
  invoiceeId?: number;
  shippingInstruction?: string;
  defaultShippingMarkFront?: string;
  defaultShippingMarkSide?: string;
}

const CustomerFormWizardContent = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { modal, notification, message: _message } = App.useApp();
  const { t, i18n } = useTranslation('customers');
  const { t: tCommon } = useTranslation('common');
  const { setHasUnsavedChanges: setGlobalUnsavedChanges } = useNavigationGuard();
  const { confirmUnsavedChanges } = useUnsavedChangesModal();

  // Edit mode detection
  const isEditMode = !!customerId;
  // Tolerant of prefixed string list ids (same class as D-VEND-NEW-1)
  const numericCustomerId = parseEntityRouteId(customerId);

  // Helper to check if an item is newly created (not yet saved to API)
  // Items from API have IDs like "contact-123", "location-456" (small numbers)
  // New items have IDs like "contact-1732625440905" (timestamp - large numbers > 1000000000)
  const isNewItem = (id: string): boolean => {
    // Safety check: if id is not a valid string, treat as new to avoid errors
    if (!id || typeof id !== 'string') return true;
    const match = id.match(/\d+$/);
    if (!match) return true;
    const numericPart = parseInt(match[0], 10);
    // Timestamps are > 1 billion, API IDs are typically < 1 million
    return numericPart > 1000000000;
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Track if user has manually deleted a location to prevent auto-create from re-adding
  const userDeletedLocationRef = useRef(false);

  // Reset the deletion flag when navigating to a new customer (create or different edit)
  useEffect(() => {
    userDeletedLocationRef.current = false;
  }, [customerId]);

  const [stepErrors, setStepErrors] = useState<{ [key: number]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  // #91 — contacts are created/edited ONLY in the Contacts module; the wizard
  // renders a view-only contact list (edit mode) and links out for creation.

  // Location state
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<CustomerLocation | null>(null);
  const [editingLocationFormData, setEditingLocationFormData] = useState<LocationFormData | null>(
    null
  );
  // For edit mode: trigger API fetch when editing existing location
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null);

  // Include inactive filters (edit mode only)
  const [includeInactiveContacts, setIncludeInactiveContacts] = useState(false);
  const [includeInactiveLocations, setIncludeInactiveLocations] = useState(false);

  const [_windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Customer existence check state
  const [countryId, setCountryId] = useState<number | null>(null);
  const [localTaxId, setLocalTaxId] = useState<string | null>(null);
  const [_hasProcessedCheck, _setHasProcessedCheck] = useState(false);

  // Debounce the localTaxId to avoid excessive API calls while user is typing
  const debouncedLocalTaxId = useDebounce(localTaxId, { wait: 800 });

  // ============================================================================
  // VALIDATION STATE MACHINE using useReducer
  // ============================================================================

  type ValidationMachineState = {
    status:
      | 'IDLE'
      | 'VALIDATING'
      | 'CUSTOMER_EXISTS'
      | 'COMPANY_FOUND'
      | 'MANUAL_ENTRY'
      | 'PRE_FILLED';
    localTaxId: string | null;
    companyName: string | null;
    message: string | null;
    showModal: boolean;
    showWarningBanner: boolean;
  };

  type ValidationMachineAction =
    | { type: 'START_VALIDATION'; localTaxId: string }
    | {
        type: 'VALIDATION_COMPLETE';
        result: 'customer_exists' | 'company_found' | 'not_found';
        customerName?: string;
        message?: string;
      }
    | { type: 'USER_TYPING' }
    | { type: 'MODAL_ACCEPTED' }
    | { type: 'MODAL_CANCELLED' }
    | { type: 'COUNTRY_CHANGED' }
    | { type: 'RESET' };

  const validationMachineReducer = (
    state: ValidationMachineState,
    action: ValidationMachineAction
  ): ValidationMachineState => {
    console.log('[Validation FSM]', action.type, 'from state:', state.status);

    switch (action.type) {
      case 'USER_TYPING':
        // User is typing - reset to idle and close modal
        return {
          ...state,
          status: 'IDLE',
          showModal: false,
          message: null,
        };

      case 'START_VALIDATION':
        // Debounce completed, start validation
        return {
          ...state,
          status: 'VALIDATING',
          localTaxId: action.localTaxId,
          showModal: false,
        };

      case 'VALIDATION_COMPLETE':
        if (action.result === 'customer_exists') {
          return {
            ...state,
            status: 'CUSTOMER_EXISTS',
            companyName: action.customerName || null, // Store vendor name for i18n
            message: null, // Don't use API message, use translation in UI
            showModal: false,
            showWarningBanner: false,
          };
        } else if (action.result === 'company_found') {
          return {
            ...state,
            status: 'COMPANY_FOUND',
            message: action.message || 'Company found',
            showModal: true, // Show modal immediately
            showWarningBanner: false,
          };
        } else {
          return {
            ...state,
            status: 'MANUAL_ENTRY',
            message: null,
            showModal: false,
            showWarningBanner: false,
          };
        }

      case 'MODAL_ACCEPTED':
        // User clicked "Use This Company" - data is pre-filled in handleLoadCompanyData
        return {
          ...state,
          status: 'PRE_FILLED',
          showModal: false,
          showWarningBanner: true,
        };

      case 'MODAL_CANCELLED':
        // User cancelled modal - go to manual entry
        return {
          ...state,
          status: 'MANUAL_ENTRY',
          showModal: false,
          message: null,
        };

      case 'COUNTRY_CHANGED':
      case 'RESET':
        // Reset everything
        return {
          status: 'IDLE',
          localTaxId: null,
          companyName: null,
          message: null,
          showModal: false,
          showWarningBanner: false,
        };

      default:
        return state;
    }
  };

  const [validationMachine, dispatchValidation] = useReducer(validationMachineReducer, {
    status: 'IDLE',
    localTaxId: null,
    companyName: null,
    message: null,
    showModal: false,
    showWarningBanner: false,
  });

  // Loading state for company data modal
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);

  // Track window height changes (currently using CSS calc(), but available for future enhancements)
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories from API (reusable function for refresh)
  // Only fetch active categories (isActive=1) since inactive categories shouldn't be assignable
  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await getCategories({ isActive: 1, pageSize: -1 }); // All active categories (pageSize: -1 = all)
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      notification.error({
        message: t('customer.notifications.systemError'),
        description: t('customer.notifications.categoriesLoadFailed'),
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setLoadingCategories(false);
    }
  }, [t, notification]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    watch,
    setValue,
    trigger,
    getValues,
    reset,
  } = useForm<FormValues>({
    mode: 'onTouched', // Validate on first blur, then on every change after
    reValidateMode: 'onChange', // Re-validate on change after first error
    defaultValues: {
      // Step 1: Primary Information
      name: '',
      alternativeName: '',
      nickname: '',
      individual: false, // Default to company (false = Company, true = Individual)
      categoryIds: [],
      country: '',
      stateId: null,
      state: '',
      cityId: null,
      city: '',
      address: '',
      postcode: '',
      phoneCountryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: '',
      phoneExtension: '',
      fax: '',
      email: '',
      localTaxId: '',
      web: '',
      // Relationships
      internalContactUserId: undefined,
      parentCompanyId: undefined,
      // Notes
      accountNotes: '',
      deliveryNotes: '',
      salesNotes: '',
      // Online account
      isActiveOnlineAccount: false,
      // Collections
      contacts: [],
      locations: [],
      // Step 2: Financial Information - Account Information
      deposit: undefined,
      cashDiscount: undefined,
      basePaymentTermId: undefined,
      dueInDays: undefined,
      creditLimit: undefined,
      hold: 'auto', // Default hold status (Auto)
      primaryCurrency: 'AUD', // Default primary currency
      // Tax Information
      taxTypeId: undefined,
      // Step 3: Preference (TODO: These should be removed or handled separately)
      invoiceeId: undefined,
      shippingInstruction: '',
      defaultShippingMarkFront: '',
      defaultShippingMarkSide: '',
    },
  });

  const selectedCountry = watch('country');
  const selectedStateId = watch('stateId');
  const selectedLocalTaxId = watch('localTaxId');
  const watchedPaymentTermId = watch('basePaymentTermId');
  const contacts = watch('contacts') || [];
  const locations = watch('locations') || [];

  // Customer mutations
  const { createCustomer, updateCustomer: updateCustomerMutation } = useCustomerMutations();

  // Payment mutations for edit mode
  // Uses 0 as fallback to satisfy React hooks rules (can't conditionally call hooks)
  // Safe because updatePaymentData is only called inside saveCurrentStep which guards: if (!isEditMode || !numericCustomerId)
  const { updatePaymentData } = useCustomerPaymentMutations(numericCustomerId ?? 0);

  // Load customer data if in edit mode
  const { customer: existingCustomer, isLoading: isLoadingCustomer } =
    useCustomerDetail(numericCustomerId);

  // Load related data when in edit mode
  const { contacts: existingContacts, isLoading: isLoadingContacts } = useCustomerContacts(
    isEditMode ? numericCustomerId : null
  );
  const { locations: existingLocations, isLoading: isLoadingLocations } = useCustomerLocations(
    isEditMode ? numericCustomerId : null
  );
  const { paymentData: existingPaymentData, isLoading: isLoadingPayment } = useCustomerPayment(
    isEditMode ? numericCustomerId : null
  );

  // Fetch individual location detail for edit modal (only when editing existing items in edit mode)
  const { location: locationDetail, isLoading: locationDetailLoading } = useCustomerLocationDetail(
    isEditMode ? numericCustomerId : null,
    editingLocationId
  );

  // Transform location detail from API to form data
  const locationDetailFormData = useMemo((): LocationFormData | null => {
    if (!locationDetail) return null;
    return {
      locationName: locationDetail.locationName,
      isDefaultShipping: locationDetail.isDefaultShipping,
      isDefaultBilling: locationDetail.isDefaultBilling,
      country: locationDetail.country,
      countryId: locationDetail.countryId ?? null,
      stateId: locationDetail.stateId ?? null,
      state: locationDetail.state ?? '',
      cityId: locationDetail.cityId ?? null,
      city: locationDetail.city ?? '',
      postcode: locationDetail.postcode ?? '',
      address: locationDetail.address ?? '',
      addressLang: locationDetail.addressLang ?? null,
      notes: locationDetail.notes ?? '',
    };
  }, [locationDetail]);

  // Get permissions for the customer - allows checking item-level permissions
  // Pass isCreateMode to enable all actions when creating a new customer
  const { getItemPermissions } = useCustomerPermissions(existingCustomer, {
    isCreateMode: !isEditMode,
  });

  // Use existing hook for customer existence check (with debounced tax ID) - only in create mode
  const {
    exists,
    companyExists,
    existingCustomerId: _existingCustomerIdFromCheck,
    existingCompany: existingCompanyFromCheck,
    message: checkMessage,
    isLoading: isCheckingCustomer,
  } = useCheckCustomerExists(isEditMode ? null : countryId, isEditMode ? '' : debouncedLocalTaxId);

  // Fetch payment terms and tax types from API (typeId: 2 = Customer)
  const { paymentTerms, isLoading: isLoadingPaymentTerms } = usePaymentTerms(2);
  const { currencyOptions, isLoading: isLoadingCurrencies } = useCurrencies();
  const { taxTypes, isLoading: isLoadingTaxTypes } = useTaxTypes(countryId ?? undefined);

  // Fetch customer options for Parent Company dropdown (exclude current customer in edit mode)
  const { customers: customerOptions, isLoading: isLoadingCustomerOptions } = useCustomerOptions(
    isEditMode ? (numericCustomerId ?? undefined) : undefined
  );

  // TODO: Fetch users for Internal Contact dropdown (需要 useUsers hook)
  // For now, use empty array until users API is implemented
  const users: { userId: number; fullName: string }[] = [];
  const isLoadingUsers = false;

  // Track initialization to avoid re-running (use state instead of ref to trigger re-renders)
  const [hasLoadedEditData, setHasLoadedEditData] = useState(false); // For edit mode 3-stage loading
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false); // For form defaults

  // Mark form as initialized after default values are set
  useEffect(() => {
    // Mark as initialized after first render
    if (!hasInitializedDefaults) {
      setHasInitializedDefaults(true);
      // Only set form as initialized in create mode (edit mode will set this after Stage 1)
      if (!isEditMode) {
        setIsFormInitialized(true);
      }
    }
  }, [hasInitializedDefaults, isEditMode]);

  // Track form dirty state (only after form is initialized)
  useEffect(() => {
    if (isFormInitialized) {
      setHasUnsavedChanges(isDirty);
    }
  }, [isDirty, isFormInitialized]);

  // Sync local unsaved changes state with global context
  useEffect(() => {
    setGlobalUnsavedChanges(hasUnsavedChanges);
    // Cleanup on unmount
    return () => {
      setGlobalUnsavedChanges(false);
    };
  }, [hasUnsavedChanges, setGlobalUnsavedChanges]);

  // Auto-select first payment term as default (only in create mode)
  useEffect(() => {
    if (isEditMode) return;
    if (paymentTerms.length > 0 && !watch('basePaymentTermId')) {
      const firstTerm = paymentTerms[0];
      setValue('basePaymentTermId', firstTerm.paymentTermId, { shouldDirty: false });
      setValue('dueInDays', firstTerm.dueDays, { shouldDirty: false });
    }
  }, [paymentTerms, isEditMode, setValue, watch]);

  // Auto-select first tax type as default (only in create mode)
  useEffect(() => {
    if (isEditMode) return;
    if (taxTypes.length > 0 && !watch('taxTypeId')) {
      setValue('taxTypeId', taxTypes[0].taxTypeId, { shouldDirty: false });
    }
  }, [taxTypes, isEditMode, setValue, watch]);

  // FSM Effect 1: User typing - dispatch USER_TYPING action
  useEffect(() => {
    if (isEditMode) return;

    // Update localTaxId for validation hook
    setLocalTaxId(selectedLocalTaxId || null);

    // If user is typing (value doesn't match debounced), dispatch USER_TYPING
    if (selectedLocalTaxId && selectedLocalTaxId !== debouncedLocalTaxId) {
      dispatchValidation({ type: 'USER_TYPING' });
    }
  }, [selectedLocalTaxId, debouncedLocalTaxId, isEditMode]);

  // FSM Effect 2: Debounce complete - start validation
  useEffect(() => {
    if (isEditMode) return;
    if (!debouncedLocalTaxId || !countryId) {
      dispatchValidation({ type: 'RESET' });
      return;
    }

    // Debounced value ready - start validation
    dispatchValidation({ type: 'START_VALIDATION', localTaxId: debouncedLocalTaxId });
  }, [debouncedLocalTaxId, countryId, isEditMode]);

  // FSM Effect 3: Validation results - dispatch VALIDATION_COMPLETE
  useEffect(() => {
    if (isEditMode) return;
    if (validationMachine.status !== 'VALIDATING') return;
    if (isCheckingCustomer) return; // Still loading

    // Validation complete - dispatch result
    if (exists) {
      // Customer already exists - show error
      dispatchValidation({
        type: 'VALIDATION_COMPLETE',
        result: 'customer_exists',
        customerName: existingCompanyFromCheck?.companyName,
      });
    } else if (companyExists && existingCompanyFromCheck) {
      // Company exists but is not registered as a customer - show modal to use existing data
      dispatchValidation({
        type: 'VALIDATION_COMPLETE',
        result: 'company_found',
        message: checkMessage || 'Company found',
      });
    } else {
      dispatchValidation({
        type: 'VALIDATION_COMPLETE',
        result: 'not_found',
      });
    }
  }, [
    validationMachine.status,
    isCheckingCustomer,
    exists,
    companyExists,
    existingCompanyFromCheck,
    checkMessage,
    isEditMode,
  ]);

  // Fetch cascading location data
  // states/cities are only used to resolve display names when the postcode
  // lookup pre-resolved the parse-output ids (board2-#79)
  const { countries, states, cities, isLoadingCountries } = useCascadingLocations(
    selectedCountry || null,
    selectedStateId
  );

  // Handle country change — clear the stale state/city PARSE OUTPUTS
  // (board2-#79: no state/city Selects; a country change invalidates any
  // previously parsed split, the backend re-parses on save)
  const handleCountryChange = useCallback(
    (value: string) => {
      setValue('country', value);
      setValue('stateId', null);
      setValue('state', '');
      setValue('cityId', null);
      setValue('city', '');

      // Update countryId for validation
      const country = countries.find((c) => c.countryCode === value);
      setCountryId(country?.countryId || null);

      // Reset validation machine when country changes
      dispatchValidation({ type: 'COUNTRY_CHANGED' });
      setStepErrors((prev) => ({ ...prev, 0: false }));
    },
    [setValue, countries]
  );

  // Extract countryId from country code
  useEffect(() => {
    if (selectedCountry) {
      if (countries.length > 0) {
        const country = countries.find((c) => c.countryCode === selectedCountry);
        if (country) {
          setCountryId(country.countryId);
        } else {
          setCountryId(null);
        }
      } else {
        // Countries not loaded yet - show warning if user is trying to check customer
        if (selectedLocalTaxId && selectedLocalTaxId.trim().length > 0) {
          console.warn('Cannot check customer existence: countries data not loaded');
        }
        setCountryId(null);
      }
    } else {
      setCountryId(null);
    }
  }, [selectedCountry, countries, selectedLocalTaxId]);

  // Update localTaxId when form value changes
  useEffect(() => {
    setLocalTaxId(selectedLocalTaxId || null);
  }, [selectedLocalTaxId]);

  // Watch values for auto-create location logic
  const watchedStateId = watch('stateId');
  const watchedCityId = watch('cityId');
  const watchedAddress = watch('address');
  const watchedPostCode = watch('postcode');
  const watchedLocationsRaw = watch('locations');

  // Reactive auto-create location effect with touchedFields-based pattern
  // This effect watches ALL required conditions and triggers auto-create when:
  // 1. All required field VALUES are filled
  // 2. All required fields have been TOUCHED (focused + blurred = user committed the field)
  //
  // board2-#79 single-address entry: the committed set is country / address /
  // postcode — state/city are backend-parse OUTPUTS and no longer gate the
  // auto-create (the postcode lookup may pre-resolve them; otherwise the row
  // is created unparsed and the backend/mock parse fills them on save).
  //
  // Key insight: touchedFields tracks which fields user has explicitly interacted with and left.
  // - For Select fields: touched when user makes a selection
  // - For Input fields: touched when user blurs (finishes typing)
  // This handles ANY field order without needing debounce.
  useEffect(() => {
    // Skip if:
    // - Form not initialized (avoid triggering on mount)
    // - User has manually deleted a location (respect user's decision)
    // - Already has locations (auto-create is one-time only)
    if (
      !isFormInitialized ||
      userDeletedLocationRef.current ||
      (watchedLocationsRaw || []).length > 0
    ) {
      return;
    }

    // Check all field VALUES are filled (country + address + postcode)
    const hasAllRequiredValues = countryId && watchedAddress && watchedPostCode;

    if (!hasAllRequiredValues) {
      return;
    }

    // Check all required fields have been TOUCHED (user committed each field)
    // This ensures we don't trigger while user is still typing
    const allFieldsTouched =
      touchedFields.country && touchedFields.address && touchedFields.postcode;

    if (!allFieldsTouched) {
      return;
    }

    // All conditions met - resolve display names and create location immediately.
    // State/city names resolve only when the postcode lookup already filled the
    // ids; an unparsed row keeps nulls (display components render a dash).
    const countryName = countries.find((c) => c.countryId === countryId)?.countryName || '';
    const stateName = watchedStateId
      ? states.find((s) => s.stateId === watchedStateId)?.stateName || ''
      : '';
    const cityName = watchedCityId
      ? cities.find((c) => c.cityId === watchedCityId)?.cityName || ''
      : '';

    const firstLocation: CustomerLocation = {
      id: `location-registered-address`,
      isDefaultShipping: true,
      isDefaultBilling: true,
      locationName: 'Registered Address',
      countryId: countryId,
      country: countryName,
      stateId: watchedStateId ?? null,
      state: stateName,
      cityId: watchedCityId ?? null,
      city: cityName,
      postcode: watchedPostCode,
      address: watchedAddress,
      addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en',
      notes: 'Auto-created from registered address',
    };

    setValue('locations', [firstLocation], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    notification.info({
      message: t('customer.messages.locationCreated'),
      description: t('customer.messages.locationAutoCreatedDescription'),
      icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
      placement: 'topRight',
      duration: 4,
    });
  }, [
    isFormInitialized,
    countryId,
    watchedStateId,
    watchedCityId,
    watchedAddress,
    watchedPostCode,
    watchedLocationsRaw,
    states,
    cities,
    countries,
    touchedFields.country,
    touchedFields.address,
    touchedFields.postcode,
    i18n.language,
    setValue,
    notification,
    token,
    t,
  ]);

  // Handle loading company data when user accepts the Company Found modal
  // Pre-fills form with existing company data WITHOUT creating a customer
  const handleLoadCompanyData = () => {
    if (!existingCompanyFromCheck) {
      console.error('No existing company data to load');
      return;
    }

    setIsLoadingCompanyData(true);
    try {
      const companyData = existingCompanyFromCheck;

      // Basic Information - pre-fill from existing company
      setValue('name', companyData.companyName || '');
      setValue('alternativeName', companyData.alternativeName || '');
      setValue('nickname', companyData.nickname || '');
      if (companyData.isIndividual !== undefined) {
        setValue('individual', companyData.isIndividual);
      }

      // Registered Address - use shouldTouch to trigger auto-create location.
      // board2-#79 single-address entry: pre-fill only country/address/postcode
      // (the auto-create touched gate); stateId/cityId are backend-parse
      // OUTPUTS and are no longer set here.
      const currentCountry = getValues('country');
      if (currentCountry) {
        // Country was committed before the check ran — re-set it with
        // shouldTouch so the auto-create gate sees it after pre-fill
        setValue('country', currentCountry, { shouldTouch: true });
      }
      setValue('address', companyData.address || '', { shouldTouch: true });
      setValue('postcode', companyData.postcode || '', { shouldTouch: true });

      // Contact Details
      if (companyData.phoneCountryCode) {
        setValue('phoneCountryCode', companyData.phoneCountryCode);
      }
      if (companyData.phoneNumber) {
        setValue('phoneNumber', companyData.phoneNumber);
      }
      if (companyData.phoneExtension) {
        setValue('phoneExtension', companyData.phoneExtension);
      }
      setValue('email', companyData.email || '');
      setValue('fax', companyData.fax || '');
      setValue('web', companyData.web || '');

      // Clear step 0 validation errors since form is now populated
      setStepErrors((prev) => ({ ...prev, 0: false }));

      // Clear existing locations and reset the deletion flag so auto-create can work with pre-filled data
      // The reactive useEffect will automatically trigger auto-create when all conditions are met
      setValue('locations', []);
      userDeletedLocationRef.current = false;

      // Dispatch modal accepted - transitions to PRE_FILLED state with banner
      dispatchValidation({ type: 'MODAL_ACCEPTED' });
    } catch (error) {
      console.error('Error loading company data:', error);
      notification.error({
        message: t('customer.notifications.loadCompanyDataFailed'),
        description: t('customer.notifications.loadCompanyDataFailedDescription'),
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setIsLoadingCompanyData(false);
    }
  };

  // Handle canceling company data modal
  const handleCancelCompanyModal = () => {
    // Dispatch modal cancelled - transitions to MANUAL_ENTRY state
    dispatchValidation({ type: 'MODAL_CANCELLED' });
    // Clear ABN field
    setValue('localTaxId', '');
    setLocalTaxId(null);
  };

  // FSM Effect 4: Update step errors based on FSM state
  useEffect(() => {
    // Only set step error when vendor exists
    if (validationMachine.status === 'CUSTOMER_EXISTS') {
      setStepErrors((prev) => ({ ...prev, 0: true }));
    } else {
      setStepErrors((prev) => ({ ...prev, 0: false }));
    }
  }, [validationMachine.status]);

  // Stage 1: Initialize form with edit mode data when ready
  // Uses reset() for a single batch update (more efficient than multiple setValue calls)
  // Note: We compute form values inside the effect to avoid dependency on memoized objects
  // which could cause unnecessary re-runs due to reference changes
  useEffect(() => {
    // Guard conditions - only run once when all data is ready
    console.log('[Edit Mode Init] Check:', {
      isEditMode,
      hasExistingCustomer: !!existingCustomer,
      countriesCount: countries.length,
      hasLoadedEditData,
      existingCustomerData: existingCustomer,
    });
    if (
      !isEditMode ||
      !existingCustomer ||
      countries.length === 0 ||
      hasLoadedEditData ||
      isLoadingPayment || // Wait for payment data to load
      isLoadingLocations || // Wait for locations to load
      isLoadingContacts // Wait for contacts to load
    ) {
      return;
    }
    console.log('[Edit Mode Init] Initializing form with:', existingCustomer);
    console.log(
      '[Edit Mode Init] Found country:',
      countries.find((c) => c.countryId === existingCustomer.countryId)
    );
    console.log(
      '[Edit Mode Init] Country code to set:',
      countries.find((c) => c.countryId === existingCustomer.countryId)?.countryCode
    );

    // Format contacts from API
    const formattedContacts =
      existingContacts?.map((contact) => ({
        id: `contact-${contact.contactId}`,
        name: contact.name,
        jobTitle: contact.jobTitle ?? '',
        phoneCountryCode: contact.phoneCountryCode ?? DEFAULT_COUNTRY_CODE,
        phoneNumber: contact.phoneNumber ?? '',
        phoneExtension: contact.phoneExtension ?? '',
        mobileCountryCode: contact.mobileCountryCode ?? undefined,
        mobileNumber: contact.mobileNumber ?? undefined,
        fax: contact.fax ?? '',
        email: contact.email,
        // IMPORTANT: Store countryId (number) for API, country (string) for display
        countryId: contact.countryId ?? undefined,
        country: contact.country ?? undefined,
        stateId: contact.stateId ?? undefined,
        state: contact.state ?? undefined,
        cityId: contact.cityId ?? undefined,
        city: contact.city ?? undefined,
        postcode: contact.postcode ?? undefined,
        address: contact.address ?? '',
        addressLang: contact.addressLang ?? undefined,
        isPrimary: contact.isPrimary,
        isActive: contact.isActive,
      })) || [];

    // Format locations from API
    const formattedLocations =
      existingLocations?.map((location) => ({
        id: `location-${location.locationId}`,
        locationName: location.locationName,
        // IMPORTANT: Store countryId (number) for API, country (string) for display
        countryId: location.countryId,
        country: location.country,
        stateId: location.stateId,
        state: location.state,
        cityId: location.cityId,
        city: location.city,
        postcode: location.postcode,
        address: location.address,
        addressLang: location.addressLang,
        notes: location.notes || '',
        isDefaultShipping: location.isDefaultShipping,
        isDefaultBilling: location.isDefaultBilling,
        isActive: location.isActive,
      })) || [];

    // Use reset() to set all form values at once
    // This also updates defaultValues so isDirty works correctly
    reset({
      // Step 1: Primary Information
      name: existingCustomer.name || '',
      alternativeName: existingCustomer.alternativeName || '',
      nickname: existingCustomer.nickname || '',
      individual: existingCustomer.individual,
      categoryIds: existingCustomer.categories?.map((c) => c.categoryId) || [],
      country: countries.find((c) => c.countryId === existingCustomer.countryId)?.countryCode || '',
      // board2-#79: stateId/cityId are backend-parse OUTPUTS and are NOT
      // pre-filled into the form. Not sending explicit ids on save lets the
      // backend (and the mock parse) re-split the merged country/postcode/
      // address values.
      stateId: null,
      state: '',
      cityId: null,
      city: '',
      address: existingCustomer.address || '',
      postcode: existingCustomer.postcode || '',
      phoneCountryCode: existingCustomer.phoneCountryCode || DEFAULT_COUNTRY_CODE,
      phoneNumber: existingCustomer.phoneNumber || '',
      phoneExtension: existingCustomer.phoneExtension || '',
      fax: existingCustomer.fax || '',
      email: existingCustomer.email || '',
      localTaxId: existingCustomer.localTaxId || '',
      web: existingCustomer.web || '',
      // Relationships
      internalContactUserId: existingCustomer.internalContact?.userId || undefined,
      parentCompanyId:
        existingCustomer.parentCompany?.companyId ??
        (existingCustomer as unknown as { parentCompanyId?: number }).parentCompanyId ??
        undefined,
      // Online account
      isActiveOnlineAccount: existingCustomer.isActiveOnlineAccount || false,
      // Notes
      accountNotes: existingCustomer.accountNotes || '',
      deliveryNotes: existingCustomer.deliveryNotes || '',
      salesNotes: existingCustomer.salesNotes || '',
      // Collections
      contacts: formattedContacts,
      locations: formattedLocations,

      // Step 2: Financial Information (from payment API)
      deposit: existingPaymentData?.depositPercentage ?? undefined,
      cashDiscount: existingPaymentData?.cashDiscountPercentage ?? undefined,
      // Use basePaymentTermId from paymentTerm object (new API structure)
      basePaymentTermId: existingPaymentData?.paymentTerm?.basePaymentTermId ?? undefined,
      dueInDays: existingPaymentData?.dueInDays ?? undefined,
      creditLimit: existingPaymentData?.creditLimit ?? undefined,
      // API returns holdStatus as number, convert to string enum for form
      hold: numberToHoldStatus(existingPaymentData?.holdStatus),
      primaryCurrency: existingPaymentData?.primaryCurrency || 'AUD',
      // Tax fields are now flat (not nested object)
      taxTypeId: existingPaymentData?.taxTypeId ?? undefined,

      // Step 3: Preference (from payment API)
      invoiceeId: existingPaymentData?.invoiceeContactId ?? undefined,
      shippingInstruction: existingPaymentData?.shippingInstruction ?? '',
      defaultShippingMarkFront: existingPaymentData?.defaultShippingMarkFront ?? '',
      defaultShippingMarkSide: existingPaymentData?.defaultShippingMarkSide ?? '',
    });

    console.log('[Edit Mode Init] Form reset complete, marking as initialized');
    // Log the actual form values after reset
    setTimeout(() => {
      console.log('[Edit Mode Init] Form values after reset:', getValues());
    }, 100);
    // Mark form as initialized
    setHasLoadedEditData(true);
    setIsFormInitialized(true);
  }, [
    isEditMode,
    existingCustomer,
    existingContacts,
    existingLocations,
    existingPaymentData,
    isLoadingPayment,
    isLoadingLocations,
    isLoadingContacts,
    countries,
    hasLoadedEditData,
    reset,
    getValues,
  ]);

  // Note: Customer entity only has countryId, not state/city. Address details come from locations.
  // The state/city fields in the form are for the registered address location, not the customer entity itself.
  // Note: Payment terms and tax types now use API-based IDs (basePaymentTermId, taxTypeId)

  // Postcode lookup handler - auto-fill state and city when user enters a postcode
  const lookupPostcode = useCallback(
    async (postcode: string) => {
      if (!selectedCountry || !postcode || postcode.length < 3) return;

      try {
        const response = await locationApi.lookupByPostcode({
          countryCode: selectedCountry,
          postcode: postcode,
        });

        if (response.data && response.data.length > 0) {
          const location = response.data[0];

          // Auto-fill state - mark as touched so auto-create location can trigger
          setValue('stateId', location.stateId, { shouldTouch: true });
          setValue('state', location.stateName, { shouldTouch: true });

          // Auto-fill city - mark as touched so auto-create location can trigger
          setValue('cityId', location.cityId, { shouldTouch: true });
          setValue('city', location.cityName, { shouldTouch: true });

          notification.success({
            message: t('customer.notifications.locationFound'),
            description: t('customer.notifications.locationAutoFilled', {
              state: location.stateName,
              city: location.cityName,
            }),
            placement: 'topRight',
            duration: 2,
          });
        }
      } catch (error) {
        console.error('Postcode lookup failed:', error);
        // Silently fail - user can still select manually
      }
    },
    [selectedCountry, setValue, notification, t]
  );

  // Debounced postcode lookup - triggers 500ms after user stops typing
  const { run: debouncedPostcodeLookup } = useDebounceFn(lookupPostcode, {
    wait: 500,
  });

  // Navigation guard for browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Check selected payment term properties (prefixed with _ as currently unused, reserved for future use)
  const selectedPaymentTerm = paymentTerms.find((t) => t.paymentTermId === watchedPaymentTermId);
  const _isZeroDayTerm = selectedPaymentTerm?.dueDays === 0;
  const _termHasDiscount = selectedPaymentTerm?.hasDiscount === true;

  const stepItems = [
    {
      title: t('customer.tabs.primaryInfo'),
      icon: <FileTextOutlined />,
      description: t('customer.stepDescriptions.primaryInfo'),
    },
    {
      title: t('customer.tabs.financialInfo'),
      icon: <DollarOutlined />,
      description: t('customer.stepDescriptions.financialInfo'),
    },
    {
      title: t('customer.tabs.preference'),
      icon: <SettingOutlined />,
      description: t('customer.stepDescriptions.preference'),
    },
  ];

  // Step validation and navigation
  const handleStepChange = async (newStep: number) => {
    // Allow going backward without confirmation (internal step navigation)
    if (newStep < currentStep) {
      setCurrentStep(newStep);
      return;
    }

    // If going forward, validate current step first
    let fieldsToValidate: Array<keyof FormValues> = [];

    if (currentStep === 0) {
      // board2-#79: address entry = country + postcode + single address
      // textfield (stateId/cityId are backend-parse outputs, not form fields)
      fieldsToValidate = [
        'country',
        'localTaxId',
        'individual',
        'name',
        'categoryIds',
        'postcode',
        'address',
        'phoneCountryCode',
        'phoneNumber',
        'email',
      ];
    } else if (currentStep === 1) {
      fieldsToValidate = ['basePaymentTermId', 'hold'];
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      // Proceed to next step
      setCurrentStep(newStep);
      setStepErrors((prev) => ({ ...prev, [currentStep]: false }));
    } else {
      setStepErrors((prev) => ({ ...prev, [currentStep]: true }));
      // Validation errors are shown inline below each field, no message needed
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      confirmUnsavedChanges(() => navigate(-1));
    } else {
      navigate(-1);
    }
  };

  // Helper function to save current step data (edit mode only)
  // Implements cumulative save pattern:
  // - Step 1: Customer + Contacts + Locations
  // - Step 2: Customer + Contacts + Locations + Financial Info
  // - Step 3: Customer + Contacts + Locations + Financial Info + Preference
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!isEditMode || !numericCustomerId) {
      return true; // Skip saving in create mode
    }

    setIsSaving(true);
    const failedOperations: string[] = [];

    try {
      const data = getValues();

      // ========================================
      // ALWAYS: Save Customer basic info
      // ========================================
      const customerData = {
        name: data.name,
        alternativeName: data.alternativeName,
        nickname: data.nickname,
        localTaxId: data.localTaxId,
        individual: data.individual,
        categoryIds: data.categoryIds,
        // IMPORTANT: customerCode is required for update
        customerCode: existingCustomer?.customerCode || '',
        ...sanitizeLocationIds({ countryId, stateId: data.stateId, cityId: data.cityId }),
        postcode: data.postcode,
        address: data.address,
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension,
        fax: data.fax,
        email: data.email,
        web: data.web,
        // Notes
        accountNotes: data.accountNotes,
        deliveryNotes: data.deliveryNotes,
        salesNotes: data.salesNotes,
        // Relationships
        internalContactUserId: data.internalContactUserId,
        parentCompanyId: data.parentCompanyId,
        // Online account
        isActiveOnlineAccount: data.isActiveOnlineAccount,
        // TODO: Financial and Preference fields moved to CustomerPaymentResponse
        // These would require separate API calls to /api/customers/:id/payment
        isActive: true,
      } as Parameters<typeof customerApi.update>[1];

      try {
        await updateCustomerMutation(numericCustomerId, customerData);
      } catch (error) {
        console.error('Failed to update customer:', error);
        failedOperations.push(t('customer.sections.basicInformation'));
      }

      // #91: contacts are no longer saved from the wizard — the Contacts
      // module owns contact create/edit. The wizard's contact list is view-only.

      // ========================================
      // ALWAYS: Save Locations (Step 1+)
      // ========================================
      if (locations.length > 0) {
        for (const location of locations) {
          try {
            const locationData = {
              locationName: location.locationName,
              ...sanitizeLocationIds({
                countryId: location.countryId,
                stateId: location.stateId,
                cityId: location.cityId,
              }),
              postcode: location.postcode,
              address: location.address,
              addressLang: location.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
              notes: location.notes,
              isDefaultShipping: location.isDefaultShipping,
              isDefaultBilling: location.isDefaultBilling,
              isActive: location.isActive !== false, // Default to active
            };

            // Check if this is a new location (ID > 1 billion = timestamp) or existing
            if (isNewItem(location.id)) {
              await customerApi.createLocation(numericCustomerId, locationData);
            } else {
              // Extract numeric ID from location.id (format: "location-123" or "location-registered-address")
              const locationIdMatch = location.id.match(/\d+$/);
              if (locationIdMatch) {
                await customerApi.updateLocation(
                  numericCustomerId,
                  parseInt(locationIdMatch[0], 10),
                  locationData
                );
              }
            }
          } catch (error) {
            console.error('Failed to save location:', location.locationName, error);
            failedOperations.push(`${t('customer.sections.locations')}: ${location.locationName}`);
          }
        }
      }

      // ========================================
      // ALWAYS: Save Payment data (Step 2 + Step 3)
      // ========================================
      if (data.taxTypeId) {
        try {
          // Build payment request - only include fields with valid values
          const paymentRequest: Parameters<typeof customerApi.updatePayment>[1] = {
            // Required fields
            taxTypeId: data.taxTypeId,
            primaryCurrency: data.primaryCurrency || 'AUD', // Required - from the financial-step Select (F2.2)
            // Hold status - convert string to number for backend (1=Auto, 2=On Hold, 3=Off Hold)
            holdStatus: holdStatusToNumber((data.hold || 'auto') as HoldStatus),
          };

          // Optional account information - only include if set
          if (data.deposit !== undefined && data.deposit !== null) {
            paymentRequest.depositPercentage = data.deposit;
          }
          if (data.cashDiscount !== undefined && data.cashDiscount !== null) {
            paymentRequest.cashDiscountPercentage = data.cashDiscount;
          }
          if (data.basePaymentTermId) {
            paymentRequest.basePaymentTermId = data.basePaymentTermId;
          }
          if (data.dueInDays !== undefined && data.dueInDays !== null) {
            paymentRequest.dueInDays = data.dueInDays;
          }
          if (data.creditLimit !== undefined && data.creditLimit !== null) {
            paymentRequest.creditLimit = data.creditLimit;
          }

          // Optional preferences - only include if non-empty
          if (data.invoiceeId) {
            paymentRequest.invoiceeContactId = data.invoiceeId;
          }
          if (data.shippingInstruction?.trim()) {
            paymentRequest.shippingInstruction = data.shippingInstruction;
          }
          if (data.defaultShippingMarkFront?.trim()) {
            paymentRequest.defaultShippingMarkFront = data.defaultShippingMarkFront;
          }
          if (data.defaultShippingMarkSide?.trim()) {
            paymentRequest.defaultShippingMarkSide = data.defaultShippingMarkSide;
          }

          // Use mutation hook which handles cache invalidation
          await updatePaymentData(paymentRequest);
        } catch (error) {
          console.error('Failed to update payment data:', error);
          failedOperations.push(t('customer.sections.financialInformation'));
        }
      }

      // ========================================
      // Show notification and navigate (only if all succeeded)
      // ========================================
      if (failedOperations.length > 0) {
        // Partial failure - stay on wizard, show warning
        notification.warning({
          message: t('customer.notifications.customerUpdatedWithWarnings'),
          description: t('customer.notifications.customerUpdatedWithWarningsDesc', {
            failedItems: failedOperations.join(', '),
          }),
          placement: 'topRight',
          duration: 8,
        });
        return false; // Stay on wizard for user to retry
      }

      // All succeeded - navigate to detail page
      notification.success({
        message: t('customer.notifications.customerUpdated'),
        description: t('customer.notifications.customerUpdatedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });

      setHasUnsavedChanges(false);
      navigate(`/auth/customers/${numericCustomerId}`);

      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      notification.error({
        message: t('customer.messages.updateFailed'),
        description: error instanceof Error ? error.message : t('messages.failedToSave'),
        placement: 'topRight',
        duration: 6,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    await handleStepChange(currentStep + 1);
  };

  const handlePrevious = async () => {
    setCurrentStep(currentStep - 1);
  };

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      // Transform form data to match API request format (CreateCustomerRequest)
      const customerData = {
        name: data.name,
        alternativeName: data.alternativeName,
        nickname: data.nickname,
        localTaxId: data.localTaxId,
        individual: data.individual,
        customerCode: 'CUS', // Backend will auto-generate the full code (e.g., CUS-0001)
        ...sanitizeLocationIds({ countryId, stateId: data.stateId, cityId: data.cityId }),
        postcode: data.postcode,
        address: data.address,
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension,
        fax: data.fax,
        email: data.email,
        web: data.web,
        internalContactUserId: data.internalContactUserId,
        parentCompanyId: data.parentCompanyId,
        isActiveOnlineAccount: data.isActiveOnlineAccount,
        categoryIds: data.categoryIds,
        // Notes
        accountNotes: data.accountNotes,
        deliveryNotes: data.deliveryNotes,
        salesNotes: data.salesNotes,
        // TODO: Financial Information and Preference moved to CustomerPaymentResponse
        // These would require a separate API call to POST /api/customers/:id/payment
        isActive: true,
      } as Parameters<typeof customerApi.create>[0];

      let savedCustomerId: number;

      if (isEditMode && numericCustomerId) {
        // Update existing customer
        await updateCustomerMutation(numericCustomerId, customerData);
        savedCustomerId = numericCustomerId;

        notification.success({
          message: t('customer.notifications.customerUpdated'),
          description: t('customer.notifications.customerUpdatedDesc'),
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          placement: 'topRight',
          duration: 3,
        });
      } else {
        // ========================================
        // CREATE MODE: Sequential API calls
        // ========================================

        // Step 1: Create customer
        const response = await createCustomer(customerData);
        savedCustomerId = response.customerId;

        // Track partial failures
        const failedOperations: string[] = [];

        // #91: the sequential contact-POST block (and its local-id → API
        // contact-id mapping for the invoicee lookup) was removed — the create
        // flow never creates contacts; they are added in the Contacts module.

        // Step 2: Create locations (if any)
        if (locations.length > 0) {
          try {
            await Promise.all(
              locations.map((location) =>
                customerApi.createLocation(savedCustomerId, {
                  locationName: location.locationName,
                  ...sanitizeLocationIds({
                    countryId: location.countryId,
                    stateId: location.stateId,
                    cityId: location.cityId,
                  }),
                  postcode: location.postcode,
                  address: location.address,
                  addressLang:
                    location.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
                  notes: location.notes,
                  isDefaultShipping: location.isDefaultShipping,
                  isDefaultBilling: location.isDefaultBilling,
                  isActive: true,
                })
              )
            );
          } catch (error) {
            console.error('Failed to create locations:', error);
            failedOperations.push(t('customer.sections.locations'));
          }
        }

        // Step 3: Create payment data (financial information + preferences)
        // Uses CreateCustomerPaymentRequest from api-spec
        // Only create payment if we have required financial data
        if (data.taxTypeId) {
          try {
            // Build payment request - only include fields with valid values
            const paymentRequest: Parameters<typeof customerApi.createPayment>[1] = {
              // Required fields
              taxTypeId: data.taxTypeId,
              primaryCurrency: data.primaryCurrency || 'AUD', // Required - from the financial-step Select (F2.2)
              // Hold status - convert string to number for backend (1=Auto, 2=On Hold, 3=Off Hold)
              holdStatus: holdStatusToNumber((data.hold || 'auto') as HoldStatus),
            };

            // Optional account information - only include if set
            if (data.deposit !== undefined && data.deposit !== null) {
              paymentRequest.depositPercentage = data.deposit;
            }
            if (data.cashDiscount !== undefined && data.cashDiscount !== null) {
              paymentRequest.cashDiscountPercentage = data.cashDiscount;
            }
            if (data.basePaymentTermId) {
              paymentRequest.basePaymentTermId = data.basePaymentTermId;
            }
            if (data.dueInDays !== undefined && data.dueInDays !== null) {
              paymentRequest.dueInDays = data.dueInDays;
            }
            if (data.creditLimit !== undefined && data.creditLimit !== null) {
              paymentRequest.creditLimit = data.creditLimit;
            }

            // Optional preferences - only include if non-empty
            // #91: no invoicee in create mode — the wizard can no longer create
            // contacts inline, so the invoicee Select has no options until the
            // customer exists and its contacts are added in the Contacts module.
            // The invoicee is then set from the customer detail (Edit Preference).
            if (data.shippingInstruction?.trim()) {
              paymentRequest.shippingInstruction = data.shippingInstruction;
            }
            if (data.defaultShippingMarkFront?.trim()) {
              paymentRequest.defaultShippingMarkFront = data.defaultShippingMarkFront;
            }
            if (data.defaultShippingMarkSide?.trim()) {
              paymentRequest.defaultShippingMarkSide = data.defaultShippingMarkSide;
            }

            await customerApi.createPayment(savedCustomerId, paymentRequest);
          } catch (error) {
            console.error('Failed to create payment data:', error);
            failedOperations.push(t('customer.sections.financialInformation'));
          }
        }

        // Final notification
        if (failedOperations.length > 0) {
          notification.warning({
            message: t('customer.notifications.customerCreatedWithWarnings'),
            description: t('customer.notifications.customerCreatedWithWarningsDesc', {
              failedItems: failedOperations.join(', '),
            }),
            placement: 'topRight',
            duration: 8,
          });
        } else {
          notification.success({
            message: t('customer.notifications.customerCreated'),
            description: t('customer.notifications.customerCreatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        }
      }

      setHasUnsavedChanges(false);

      // Navigate to customer detail page
      navigate(`/auth/customers/${savedCustomerId}`);
    } catch (error) {
      console.error('Error saving customer:', error);

      // Show error in notification
      const errorMessage = error instanceof Error ? error.message : t('messages.failedToSave');

      notification.error({
        message: isEditMode
          ? t('customer.messages.updateFailed')
          : t('customer.messages.createFailed'),
        description: errorMessage,
        placement: 'topRight',
        duration: 6,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // #91: all wizard contact handlers (add/edit/set-primary/delete/toggle) were
  // removed — contact create/edit lives exclusively in the Contacts module.

  // Location Handlers
  const handleAddLocation = (locationData: LocationFormData) => {
    // Check if setting as default shipping
    const wantsToSetDefault = locationData.isDefaultShipping === true;

    // Check if there's already a default location
    const currentDefaultIndex = locations.findIndex((l) => l.isDefaultShipping);
    const hasExistingDefault = currentDefaultIndex !== -1;
    const isEditingCurrentDefault = editingLocation?.id === locations[currentDefaultIndex]?.id;

    // If user wants to set this as default and there's already a different default location
    if (wantsToSetDefault && hasExistingDefault && !isEditingCurrentDefault) {
      modal.confirm({
        title: t('customer.notifications.defaultLocationUpdated'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: (
          <Text>
            {t('common:messages.replaceDefaultLocationPrefix')}
            <Text strong>{locations[currentDefaultIndex].locationName}</Text>
            {t('common:messages.replaceDefaultLocationSuffix')}
          </Text>
        ),
        okText: t('customer.modals.replace'),
        okType: 'primary',
        cancelText: t('customer.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: () => {
          saveLocation(locationData, true);
          setLocationModalOpen(false);
        },
        onCancel: () => {
          // User cancelled - don't save, keep location modal open
        },
      });
    } else {
      // No conflict, save normally
      saveLocation(locationData, wantsToSetDefault);
      setLocationModalOpen(false);
    }
  };

  // Helper function to save location after default logic is resolved
  // Now uses customer-specific LocationFormData with isDefaultShipping and isDefaultBilling
  const saveLocation = (locationData: LocationFormData, isDefault: boolean) => {
    // Use countryId directly from form data (modal provides it via CascadingLocationFields)
    const resolvedCountryId = locationData.countryId ?? null;

    if (editingLocation) {
      // Update existing location
      // Preserve existing addressLang if editing, otherwise use current UI language
      const updatedLocation: CustomerLocation = {
        ...editingLocation,
        isDefaultShipping: isDefault,
        isDefaultBilling: locationData.isDefaultBilling,
        locationName: locationData.locationName,
        country: locationData.country,
        countryId: resolvedCountryId,
        stateId: locationData.stateId,
        state: locationData.state,
        cityId: locationData.cityId,
        city: locationData.city,
        postcode: locationData.postcode,
        address: locationData.address,
        addressLang:
          locationData.addressLang ||
          editingLocation.addressLang ||
          (i18n.language.startsWith('zh') ? 'zh' : 'en'),
        notes: locationData.notes || '',
      };

      // If setting this as default, remove default from all others
      const updatedLocations = locations.map((l) =>
        l.id === editingLocation.id
          ? updatedLocation
          : { ...l, isDefaultShipping: isDefault ? false : l.isDefaultShipping }
      );

      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setEditingLocation(null);
      setEditingLocationFormData(null);
      notification.success({
        message: t('customer.notifications.locationUpdated'),
        description: t('customer.notifications.locationUpdatedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    } else {
      // Add new location
      const newLocation: CustomerLocation = {
        id: `location-${Date.now()}`,
        isDefaultShipping: isDefault,
        isDefaultBilling: locationData.isDefaultBilling,
        locationName: locationData.locationName,
        country: locationData.country,
        countryId: resolvedCountryId,
        stateId: locationData.stateId,
        state: locationData.state,
        cityId: locationData.cityId,
        city: locationData.city,
        postcode: locationData.postcode,
        address: locationData.address,
        addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en',
        notes: locationData.notes || '',
      };

      // If setting this as default, remove default from all existing locations and add new at top
      // Otherwise, insert new location after default or at top if no default
      let updatedLocations: CustomerLocation[];
      if (isDefault) {
        updatedLocations = [
          newLocation,
          ...locations.map((l) => ({ ...l, isDefaultShipping: false })),
        ];
      } else {
        // Find default index and insert after it
        const defaultIndex = locations.findIndex((l) => l.isDefaultShipping);
        if (defaultIndex !== -1) {
          updatedLocations = [
            ...locations.slice(0, defaultIndex + 1),
            newLocation,
            ...locations.slice(defaultIndex + 1),
          ];
        } else {
          // No default, add at top
          updatedLocations = [newLocation, ...locations];
        }
      }

      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notification.success({
        message: t('customer.notifications.locationAdded'),
        description: t('customer.notifications.locationAddedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    }

    setHasUnsavedChanges(true);
  };

  const handleEditLocation = (location: CustomerLocation) => {
    setEditingLocation(location);

    // Extract the numeric location ID from the string ID (e.g., "location-123" -> 123)
    const locationIdMatch = location.id.match(/\d+$/);
    const isExistingLocation = locationIdMatch && !isNewItem(location.id);

    if (isEditMode && isExistingLocation) {
      // For existing locations in edit mode, fetch from API to get raw address data
      setEditingLocationId(parseInt(locationIdMatch[0], 10));
      setEditingLocationFormData(null); // Will be set by useMemo when API returns
    } else {
      // For new locations (create mode or newly added in edit mode), use local data
      setEditingLocationId(null);
      const formData: LocationFormData = {
        locationName: location.locationName,
        isDefaultShipping: location.isDefaultShipping ?? false,
        isDefaultBilling: location.isDefaultBilling ?? false,
        country: location.country,
        countryId: location.countryId ?? null,
        stateId: location.stateId,
        state: location.state,
        cityId: location.cityId,
        city: location.city,
        postcode: location.postcode,
        address: location.address,
        addressLang: location.addressLang ?? null,
        notes: location.notes || '',
      };
      setEditingLocationFormData(formData);
    }

    setLocationModalOpen(true);
  };

  const handleSetAsDefaultLocation = (locationId: string) => {
    const currentDefault = locations.findIndex((l) => l.isDefaultShipping);
    const locationIndex = locations.findIndex((l) => l.id === locationId);

    // Helper function to set default (called after confirmation if needed)
    // Only updates local state - API call happens when user clicks Save
    const setAsDefault = () => {
      // Update local state
      const updatedLocations = locations.map((location) => ({
        ...location,
        isDefaultShipping: location.id === locationId,
      }));
      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notification.success({
        message: t('customer.notifications.defaultLocationUpdated'),
        description: t('customer.notifications.defaultLocationSet'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    };

    if (currentDefault !== -1 && currentDefault !== locationIndex) {
      const currentDefaultLocation = locations[currentDefault];

      // Confirm replacing existing default
      modal.confirm({
        title: t('customer.notifications.defaultLocationUpdated'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: t('common:messages.replaceDefaultLocation', {
          name: currentDefaultLocation.locationName,
        }),
        okText: t('customer.modals.replace'),
        okType: 'primary',
        cancelText: t('customer.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: setAsDefault,
      });
    } else {
      // No existing default or same location - just set it
      setAsDefault();
    }
  };

  const handleSetAsDefaultBillingLocation = (locationId: string) => {
    const currentDefault = locations.findIndex((l) => l.isDefaultBilling);
    const locationIndex = locations.findIndex((l) => l.id === locationId);

    // Helper function to set default (called after confirmation if needed)
    // Only updates local state - API call happens when user clicks Save
    const setAsDefault = () => {
      // Update local state
      const updatedLocations = locations.map((location) => ({
        ...location,
        isDefaultBilling: location.id === locationId,
      }));
      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notification.success({
        message: t('customer.notifications.defaultBillingLocationSet'),
        description: t('customer.notifications.defaultBillingLocationSetDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    };

    if (currentDefault !== -1 && currentDefault !== locationIndex) {
      const currentDefaultLocation = locations[currentDefault];

      // Confirm replacing existing default
      modal.confirm({
        title: t('customer.notifications.defaultBillingLocationSet'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: t('common:messages.replaceDefaultLocation', {
          name: currentDefaultLocation.locationName,
        }),
        okText: t('customer.modals.replace'),
        okType: 'primary',
        cancelText: t('customer.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: setAsDefault,
      });
    } else {
      // No existing default or same location - just set it
      setAsDefault();
    }
  };

  const handleDeleteLocation = (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);

    // Check if the location is the default shipping
    if (location?.isDefaultShipping) {
      notification.error({
        message: t('customer.notifications.cannotDeleteDefaultShipping'),
        description: t('customer.notifications.cannotDeleteDefaultShippingDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    // Check if the location is the default billing
    if (location?.isDefaultBilling) {
      notification.error({
        message: t('customer.notifications.cannotDeleteDefaultBilling'),
        description: t('customer.notifications.cannotDeleteDefaultBillingDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    modal.confirm({
      title: t('customer.modals.deleteLocation'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorError }} />,
      content: 'Are you sure you want to delete this location?',
      okText: t('customer.buttons.delete'),
      okType: 'danger',
      cancelText: t('customer.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: () => {
        const updatedLocations = locations.filter((l) => l.id !== locationId);
        setValue('locations', updatedLocations, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setHasUnsavedChanges(true);
        // Mark that user has manually deleted a location to prevent auto-create from re-adding
        userDeletedLocationRef.current = true;
        notification.success({
          message: t('customer.notifications.locationDeleted'),
          description: t('customer.notifications.locationDeletedDesc'),
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          placement: 'topRight',
          duration: 3,
        });
      },
    });
  };

  // Toggle location status (edit mode only)
  const handleToggleLocationStatus = async (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);
    const isCurrentlyActive = location?.isActive !== false;

    // Check if trying to deactivate a default shipping location
    if (location?.isDefaultShipping && isCurrentlyActive) {
      notification.error({
        message: t('customer.notifications.cannotDeactivateDefaultShipping'),
        description: t('customer.notifications.cannotDeactivateDefaultShippingDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    // Check if trying to deactivate a default billing location
    if (location?.isDefaultBilling && isCurrentlyActive) {
      notification.error({
        message: t('customer.notifications.cannotDeactivateDefaultBilling'),
        description: t('customer.notifications.cannotDeactivateDefaultBillingDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    modal.confirm({
      title: isCurrentlyActive
        ? t('customer.modals.deactivateLocationTitle')
        : t('customer.modals.activateLocationTitle'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorWarning }} />,
      content: isCurrentlyActive
        ? t('customer.modals.deactivateLocationConfirm')
        : t('customer.modals.activateLocationConfirm'),
      okText: isCurrentlyActive ? t('customer.actions.deactivate') : t('customer.actions.activate'),
      okType: isCurrentlyActive ? 'danger' : 'primary',
      cancelText: t('customer.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: async () => {
        try {
          // For existing locations (from API), call the toggle API
          // For new locations (not yet saved), just update local state
          const locationIdMatch = locationId.match(/\d+$/);
          const isExistingLocation = locationIdMatch && !isNewItem(locationId);

          if (isEditMode && isExistingLocation && numericCustomerId) {
            // Call API to toggle the location status
            await customerApi.toggleLocationStatus(
              numericCustomerId,
              parseInt(locationIdMatch[0], 10),
              !isCurrentlyActive
            );
          }

          // Update local state
          const updatedLocations = locations.map((l) =>
            l.id === locationId ? { ...l, isActive: !isCurrentlyActive } : l
          );
          setValue('locations', updatedLocations, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });

          notification.success({
            message: isCurrentlyActive
              ? t('customer.notifications.locationDeactivated')
              : t('customer.notifications.locationActivated'),
            description: isCurrentlyActive
              ? t('customer.notifications.locationDeactivatedDesc')
              : t('customer.notifications.locationActivatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        } catch (error) {
          console.error('Error toggling location status:', error);
          notification.error({
            message: t('customer.messages.locationStatusUpdateFailed'),
            placement: 'topRight',
            duration: 4,
          });
        }
      },
    });
  };

  // Calculate heights
  const NAVBAR_HEIGHT = 48; // MainLayout navbar height (Ant Design default)
  const FOOTER_HEIGHT = 80; // Sticky bottom buttons

  // Standardized styles
  const largeButtonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
  };

  // Override formStyles with generous spacing (wizard uses more generous spacing than modals)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized form styles
  const sectionTitleStyle = formStyles.sectionTitleStyle;

  const labelStyle = {
    ...formStyles.labelStyle,
    color: token.colorTextSecondary,
    marginBottom: '4px',
  };

  // Show loading spinner when loading vendor data in edit mode
  if (
    isEditMode &&
    (isLoadingCustomer || isLoadingContacts || isLoadingLocations || false || false)
  ) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-96">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          /* Center all large select placeholders vertically */
          .ant-select-lg .ant-select-selection-placeholder {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            inset-inline-start: 11px !important;
            inset-inline-end: 11px !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
          }

          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-message-notice-content {
            min-height: ${controlHeightTokens.controlHeight}px !important;
            padding: 8px 16px !important;
            display: flex !important;
            align-items: center !important;
          }

          /* Category multi-select styling */
          .category-select-standard .ant-select-selector {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 4px 8px !important;
            border-radius: ${radiusTokens.card}px !important;
          }

          /* +N overflow tag styling */
          .category-select-standard .ant-select-selection-overflow-item-suffix .ant-select-selection-item {
            background-color: ${token.colorFillQuaternary} !important;
            color: ${token.colorTextSecondary} !important;
            border: none !important;
          }

          /* Category dropdown styling */
          .category-dropdown-elevated {
            background-color: ${token.colorBgElevated} !important;
            border: 1px solid ${token.colorBorderSecondary} !important;
            border-radius: ${radiusTokens.panel}px !important;
            box-shadow: ${token.boxShadowSecondary} !important;
          }

          .category-dropdown-elevated .ant-select-item {
            padding: 8px 12px !important;
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }

          .category-dropdown-elevated .ant-select-item .ant-select-item-option-content {
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }

          /* Sales Rep Select Styling (Match Category Select) */
          .sales-rep-select .ant-select-selector {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 4px 8px !important;
            display: flex !important;
            align-items: center !important;
          }

          .sales-rep-select .ant-select-selection-placeholder {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            inset-inline-start: 11px !important;
            inset-inline-end: 11px !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
          }
        `}
      </style>
      <Layout
        style={{
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          backgroundColor: token.colorBgLayout,
          borderTop: `1px solid ${token.colorBorder}`,
        }}
      >
        {/* Sticky Sidebar with Steps */}
        <Sider
          width={300}
          style={{
            backgroundColor: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            position: 'sticky',
            top: `${NAVBAR_HEIGHT}px`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: token.paddingLG,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: `${FOOTER_HEIGHT}px`, // Reserve space for footer alignment
              position: 'relative', // For absolute positioning of progress section
            }}
          >
            {/* Steps */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: token.paddingMD }}>
              {stepItems.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;
                const hasError = stepErrors[index];

                return (
                  <React.Fragment key={index}>
                    <div
                      onClick={() => handleStepChange(index)}
                      style={{
                        padding: token.paddingLG,
                        marginBottom: token.marginMD,
                        borderRadius: radiusTokens.panel,
                        backgroundColor: hasError
                          ? token.colorErrorBg
                          : isCurrent
                            ? token.colorPrimaryBg
                            : isCompleted
                              ? token.colorSuccessBg
                              : token.colorFillQuaternary,
                        border: `2px solid ${
                          hasError
                            ? token.colorError
                            : isCurrent
                              ? token.colorPrimary
                              : isCompleted
                                ? token.colorSuccess
                                : 'transparent'
                        }`,
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      {/* Status icons in top-right */}
                      {isCompleted && (
                        <div style={{ position: 'absolute', top: 12, right: 12 }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: 20,
                              color: token.colorSuccess,
                            }}
                          />
                        </div>
                      )}

                      {!isCompleted && !isCurrent && (
                        <div style={{ position: 'absolute', top: 12, right: 12 }}>
                          <ClockCircleOutlined
                            style={{
                              fontSize: 20,
                              color: token.colorTextDisabled,
                            }}
                          />
                        </div>
                      )}

                      <div className="tw-flex tw-items-start tw-gap-4">
                        {/* Large Icon */}
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: radiusTokens.panel,
                            backgroundColor: hasError
                              ? token.colorError
                              : isCompleted
                                ? token.colorSuccess
                                : isCurrent
                                  ? token.colorPrimary
                                  : token.colorFillTertiary,
                            color:
                              hasError || isCompleted || isCurrent
                                ? token.colorWhite
                                : token.colorTextDisabled,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: 24,
                          }}
                        >
                          {step.icon}
                        </div>

                        {/* Step Info */}
                        <div style={{ flex: 1, paddingTop: 2 }}>
                          <div
                            style={{
                              fontSize: token.fontSizeSM,
                              color: token.colorTextSecondary,
                              marginBottom: 4,
                              textTransform: 'uppercase',
                              fontWeight: token.fontWeightStrong,
                              letterSpacing: '0.5px',
                            }}
                          >
                            Step {index + 1}
                          </div>
                          <div
                            style={{
                              fontSize: token.fontSize,
                              fontWeight: token.fontWeightStrong,
                              color: hasError
                                ? token.colorError
                                : isCurrent
                                  ? token.colorPrimary
                                  : isCompleted
                                    ? token.colorSuccess
                                    : token.colorText,
                              marginBottom: 4,
                            }}
                          >
                            {step.title}
                          </div>
                          <div
                            style={{
                              fontSize: token.fontSizeSM,
                              color: token.colorTextSecondary,
                            }}
                          >
                            {step.description}
                          </div>

                          {/* Status Badge */}
                          <div style={{ marginTop: 8 }}>
                            <Badge
                              status={hasError ? 'error' : isCompleted ? 'success' : 'default'}
                              text={
                                <Text
                                  style={{
                                    fontSize: token.fontSizeSM,
                                    color: hasError
                                      ? token.colorError
                                      : isCompleted
                                        ? token.colorSuccess
                                        : isCurrent
                                          ? token.colorPrimary
                                          : token.colorTextDisabled,
                                  }}
                                >
                                  {hasError
                                    ? t('customer.wizard.status.hasErrors')
                                    : isCompleted
                                      ? t('customer.wizard.status.completed')
                                      : isCurrent
                                        ? t('customer.wizard.status.inProgress')
                                        : t('customer.wizard.status.pending')}
                                </Text>
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Progress Info - Footer structure */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: `${FOOTER_HEIGHT}px`,
                padding: `${token.paddingMD}px ${token.paddingLG}px`,
                backgroundColor: token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                zIndex: 98,
              }}
            >
              <Text
                style={{
                  display: 'block',
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  textAlign: 'center',
                }}
              >
                Step {currentStep + 1} of {stepItems.length}
              </Text>
              <div
                style={{
                  width: '100%',
                  height: 4,
                  backgroundColor: token.colorBgTextHover,
                  borderRadius: 2,
                  marginTop: token.marginSM,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${((currentStep + 1) / stepItems.length) * 100}%`,
                    height: '100%',
                    backgroundColor: token.colorPrimary,
                  }}
                />
              </div>
            </div>
          </div>
        </Sider>

        {/* Scrollable Content Area */}
        <Content
          style={{
            backgroundColor: token.colorBgLayout,
            position: 'relative',
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            overflow: 'hidden',
            boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.06), -4px 0 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div
            style={{
              height: `calc(100vh - ${NAVBAR_HEIGHT + FOOTER_HEIGHT}px)`,
              overflowY: 'auto',
              padding: token.paddingLG,
            }}
          >
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
              <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                {/* Create-mode reminder banner (#90) — CREATE only, never edit */}
                {!isEditMode && (
                  <Alert
                    type="info"
                    showIcon
                    message={t('customer.wizard.createReminder')}
                    style={{ marginBottom: '16px' }}
                  />
                )}
                {/* Step 1: Primary Information */}
                {currentStep === 0 && (
                  <div>
                    {/* Warning Banner for Pre-Filled Data */}
                    {validationMachine.showWarningBanner && (
                      <Alert
                        message={t('customer.warnings.editingExistingCompanyTitle')}
                        description={t('customer.warnings.editingExistingCompanyDescription', {
                          taxId: validationMachine.localTaxId,
                        })}
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        style={{ marginBottom: '24px' }}
                        closable
                        onClose={() => dispatchValidation({ type: 'RESET' })}
                      />
                    )}

                    {/* Basic Information Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.basicInformation')}
                      </Text>
                      {/* Row 1: Country + Tax ID (ABN) */}
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.country')}
                          </Text>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: t('customer.validation.countryRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  value={field.value}
                                  size="large"
                                  showSearch
                                  placeholder={t('customer.placeholders.country')}
                                  loading={isLoadingCountries}
                                  options={countries?.map((c) => ({
                                    label: c.countryName,
                                    value: c.countryCode,
                                  }))}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    field.onBlur(); // Mark as touched for auto-create location
                                    handleCountryChange(value);
                                    trigger('country'); // Re-validate to clear error
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.country ? token.colorError : undefined,
                                  }}
                                />
                                {errors.country && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.country.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.taxIdAbn')}
                          </Text>
                          <Controller
                            name="localTaxId"
                            control={control}
                            rules={{
                              required: t('customer.validation.taxIdRequired'),
                              minLength: {
                                value: 8,
                                message: t('customer.validation.taxIdMin'),
                              },
                            }}
                            render={({ field }) => (
                              <>
                                <div style={{ position: 'relative' }}>
                                  <Input
                                    {...field}
                                    size="large"
                                    placeholder="12345678"
                                    readOnly={isEditMode}
                                    style={{
                                      ...formStyles.inputStyle,
                                      fontFamily: 'monospace',
                                      borderColor: errors.localTaxId
                                        ? token.colorError
                                        : validationMachine.status === 'CUSTOMER_EXISTS'
                                          ? token.colorError
                                          : validationMachine.status === 'COMPANY_FOUND'
                                            ? token.colorSuccess
                                            : undefined,
                                      paddingRight: '32px', // Space for icon
                                    }}
                                  />
                                  {/* Validation Status Icons */}
                                  {validationMachine.status === 'VALIDATING' && (
                                    <LoadingOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorTextSecondary,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                  {validationMachine.status === 'CUSTOMER_EXISTS' && (
                                    <ExclamationCircleOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorError,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                  {validationMachine.status === 'COMPANY_FOUND' && (
                                    <CheckCircleOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorSuccess,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                </div>
                                {/* Validation Messages */}
                                {/*
                                  Best Practice: Show form validation errors only after field is touched (blurred)
                                  - mode: 'onBlur' ensures validation happens after blur
                                  - touchedFields check ensures we only show errors after user interaction
                                  - This prevents "required" error from showing while user is typing
                                */}
                                {errors.localTaxId && touchedFields.localTaxId && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.localTaxId.message}
                                  </Text>
                                )}
                                {!errors.localTaxId &&
                                  validationMachine.status === 'CUSTOMER_EXISTS' && (
                                    <Text
                                      style={{
                                        fontSize: token.fontSizeSM,
                                        color: token.colorError,
                                      }}
                                    >
                                      {validationMachine.companyName
                                        ? t('customer.validation.taxIdExistsWithName', {
                                            customerName: validationMachine.companyName,
                                          })
                                        : t('customer.validation.taxIdExists')}
                                    </Text>
                                  )}
                                {!errors.localTaxId &&
                                  validationMachine.status === 'COMPANY_FOUND' && (
                                    <Text
                                      style={{
                                        fontSize: token.fontSizeSM,
                                        color: token.colorSuccess,
                                      }}
                                    >
                                      {t('customer.modals.companyExistsNotCustomer')}
                                    </Text>
                                  )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Customer Type */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.type')}
                          </Text>
                          <Controller
                            name="individual"
                            control={control}
                            rules={{
                              validate: (value) =>
                                value !== undefined ||
                                t('customer.validation.customerTypeRequired'),
                            }}
                            render={({ field }) => (
                              <>
                                <Radio.Group
                                  value={field.value}
                                  onChange={(e) => field.onChange(e.target.value)}
                                  size="large"
                                  style={{ marginTop: '8px' }}
                                >
                                  <Radio value={false}>{t('customer.customerTypes.company')}</Radio>
                                  <Radio value={true} style={{ marginLeft: '24px' }}>
                                    {t('customer.customerTypes.individual')}
                                  </Radio>
                                </Radio.Group>
                                {errors.individual && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.individual.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Customer Name - full width */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.name')}
                          </Text>
                          <Controller
                            name="name"
                            control={control}
                            rules={{ required: t('customer.validation.nameRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.customerName')}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.name ? token.colorError : undefined,
                                  }}
                                />
                                {errors.name && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.name.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 4: Alternative Name + Nickname (not required) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.alternativeName')}</Text>
                          <Controller
                            name="alternativeName"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.alternativeName')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.nickname')}</Text>
                          <Controller
                            name="nickname"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.nickname')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Category */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.category')}
                          </Text>
                          <Controller
                            name="categoryIds"
                            control={control}
                            rules={{
                              required: t('customer.validation.categoryRequired'),
                              validate: (value) =>
                                (value && value.length > 0) ||
                                t('customer.validation.categoryRequired'),
                            }}
                            render={({ field, fieldState }) => (
                              <CategorySelectWithCreate
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  trigger('categoryIds'); // Re-validate to clear error
                                }}
                                categories={categories || []}
                                loading={loadingCategories}
                                onCategoryCreated={fetchCategories}
                                error={
                                  fieldState.error?.message
                                    ? t(fieldState.error.message)
                                    : undefined
                                }
                                placeholder={t('customer.placeholders.category')}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Location Section — board2-#79 single-address entry:
                        postcode + ONE full-address textfield (country lives in
                        Basic Information above). State/city Selects removed —
                        the backend parses the address (Google Maps) into stored
                        stateId/cityId; mocks simulate that split. */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.location')}
                      </Text>
                      {/* Row 1: Postcode */}
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.postcode')}
                          </Text>
                          <Controller
                            name="postcode"
                            control={control}
                            rules={{ required: t('customer.validation.postcodeRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.postcode')}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    debouncedPostcodeLookup(e.target.value);
                                  }}
                                  onBlur={field.onBlur}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.postcode ? token.colorError : undefined,
                                  }}
                                />
                                {errors.postcode && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.postcode.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: single full-address textfield (whole street
                          address in ONE field, matching the shared entry group) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.address')}
                          </Text>
                          <Controller
                            name="address"
                            control={control}
                            rules={{ required: t('customer.validation.addressRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.address')}
                                  onBlur={field.onBlur}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.address ? token.colorError : undefined,
                                  }}
                                />
                                {errors.address && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.address.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Contact Information Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.contactDetails')}
                      </Text>
                      {/* Row 1: Country Code + Phone Number + Extension */}
                      <Row gutter={[16, 16]}>
                        <Col span={6}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.countryCode')}
                          </Text>
                          <Controller
                            name="phoneCountryCode"
                            control={control}
                            rules={{ required: t('customer.validation.countryCodeRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder="+1"
                                  showSearch
                                  loading={isLoadingCountries}
                                  options={countries
                                    ?.filter((c) => c.phoneCode)
                                    .map((c) => ({
                                      label: `${c.phoneCode} ${c.countryName}`,
                                      value: c.phoneCode,
                                    }))}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    trigger('phoneCountryCode'); // Re-validate to clear error
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.phoneCountryCode
                                      ? token.colorError
                                      : undefined,
                                  }}
                                />
                                {errors.phoneCountryCode && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.phoneCountryCode.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={14}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.phoneNumber')}
                          </Text>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{ required: t('customer.validation.phoneRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.phone')}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.phoneNumber ? token.colorError : undefined,
                                  }}
                                />
                                {errors.phoneNumber && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.phoneNumber.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={4}>
                          <Text style={labelStyle}>{t('customer.fields.extension')}</Text>
                          <Controller
                            name="phoneExtension"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.extension')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Email (full width) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.email')}
                          </Text>
                          <Controller
                            name="email"
                            control={control}
                            rules={{
                              required: t('customer.validation.emailRequired'),
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('customer.validation.emailInvalidFormat'),
                              },
                            }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  type="email"
                                  placeholder={t('customer.placeholders.email')}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.email ? token.colorError : undefined,
                                  }}
                                />
                                {errors.email && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.email.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Web + Fax */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.web')}</Text>
                          <Controller
                            name="web"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.web')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.fax')}</Text>
                          <Controller
                            name="fax"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.fax')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Additional Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.additional')}
                      </Text>
                      {/* Row 1: Internal Contact (Sales Rep) + Parent Company */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.salesRep')}</Text>
                          <Controller
                            name="internalContactUserId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                onChange={(value) => field.onChange(value ?? null)}
                                size="large"
                                placeholder={t('customer.placeholders.select')}
                                style={{ ...formStyles.inputStyle, width: '100%' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                loading={isLoadingUsers}
                                options={users.map((user) => ({
                                  label: user.fullName,
                                  value: user.userId,
                                }))}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.parentCompany')}</Text>
                          <Controller
                            name="parentCompanyId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                onChange={(value) => field.onChange(value ?? null)}
                                size="large"
                                placeholder={t('customer.placeholders.select')}
                                style={{ ...formStyles.inputStyle, width: '100%' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                loading={isLoadingCustomerOptions}
                                options={customerOptions
                                  .filter((c) => !isEditMode || c.customerId !== numericCustomerId)
                                  .map((c) => ({
                                    label: c.customerName,
                                    value: c.customerId,
                                  }))}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Account Notes + Delivery Notes */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.accountNotes')}</Text>
                          <Controller
                            name="accountNotes"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.accountNotes')}
                                rows={3}
                                style={{
                                  borderRadius: `${radiusTokens.card}px`,
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.deliveryNotes')}</Text>
                          <Controller
                            name="deliveryNotes"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.deliveryNotes')}
                                rows={3}
                                style={{
                                  borderRadius: `${radiusTokens.card}px`,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Sales Notes (full width) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('customer.fields.salesNotes')}</Text>
                          <Controller
                            name="salesNotes"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.salesNotes')}
                                rows={3}
                                style={{
                                  borderRadius: `${radiusTokens.card}px`,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Contacts Section — #91 view-only: contact create/edit lives in the Contacts module */}
                    <div style={fieldContainerStyle}>
                      <div
                        className="tw-flex tw-items-center tw-justify-between"
                        style={{ marginBottom: token.marginMD }}
                      >
                        <Text style={sectionTitleStyle}>{t('customer.sections.contacts')}</Text>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          {isEditMode && (
                            <Checkbox
                              checked={includeInactiveContacts}
                              onChange={(e) => setIncludeInactiveContacts(e.target.checked)}
                            >
                              {t('customer.labels.includeInactiveContacts')}
                            </Checkbox>
                          )}
                          {/* #91 方式2 — the add button LINKS to the Contacts module */}
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/auth/contacts')}
                            style={largeButtonStyle}
                          >
                            {t('customer.contactsPolicy.addContact')}
                          </Button>
                        </div>
                      </div>
                      <Text type="secondary" style={{ display: 'block' }}>
                        {t('customer.contactsPolicy.managedInModule')}
                      </Text>
                      {contacts.length > 0 && (
                        <TableContainer>
                          <Table
                            dataSource={[...contacts]
                              .filter((c) => includeInactiveContacts || c.isActive !== false)
                              .sort((a, b) => {
                                // Primary contact always comes first
                                if (a.isPrimary && !b.isPrimary) return -1;
                                if (!a.isPrimary && b.isPrimary) return 1;
                                return 0;
                              })}
                            rowKey="id"
                            rowClassName={(record) =>
                              record.isActive === false ? 'inactive-row' : ''
                            }
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            style={{ marginTop: token.marginMD }}
                            columns={[
                              {
                                title: t('customer.table.primary'),
                                dataIndex: 'isPrimary',
                                key: 'isPrimary',
                                width: 120,
                                align: 'center',
                                render: (isPrimary: boolean) => (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: '100%',
                                      minHeight: '40px',
                                    }}
                                  >
                                    {isPrimary && (
                                      <CheckOutlined
                                        style={{
                                          color: token.colorPrimary,
                                          fontSize: token.sizeMD,
                                        }}
                                      />
                                    )}
                                  </div>
                                ),
                              },
                              {
                                title: t('customer.table.name'),
                                dataIndex: 'name',
                                key: 'name',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => <Text strong>{text}</Text>,
                              },
                              {
                                title: t('customer.table.jobTitle'),
                                dataIndex: 'jobTitle',
                                key: 'jobTitle',
                                width: 120,
                                ellipsis: true,
                              },
                              {
                                title: t('customer.table.phone'),
                                key: 'phone',
                                width: 180,
                                render: (_: unknown, record: CustomerContact) => {
                                  if (!record.phoneNumber) return '-';
                                  const parts = [record.phoneCountryCode, record.phoneNumber]
                                    .filter(Boolean)
                                    .join(' ');
                                  return record.phoneExtension
                                    ? `${parts} ext ${record.phoneExtension}`
                                    : parts;
                                },
                              },
                              {
                                title: t('customer.table.mobile'),
                                key: 'mobileNumber',
                                width: 150,
                                render: (_: unknown, record: CustomerContact) => {
                                  if (!record.mobileNumber) return '-';
                                  const parts = [record.mobileCountryCode, record.mobileNumber]
                                    .filter(Boolean)
                                    .join(' ');
                                  return parts || '-';
                                },
                              },
                              {
                                title: t('customer.table.fax'),
                                dataIndex: 'fax',
                                key: 'fax',
                                width: 150,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('customer.table.email'),
                                dataIndex: 'email',
                                key: 'email',
                                width: 260,
                                ellipsis: true,
                              },
                              {
                                title: t('customer.table.address'),
                                dataIndex: 'address',
                                key: 'address',
                                ellipsis: true,
                                render: (_: unknown, record: CustomerContact) => (
                                  <ContactAddressDisplay contact={record} />
                                ),
                              },
                            ]}
                          />
                        </TableContainer>
                      )}
                    </div>

                    {/* Locations Section */}
                    <div style={fieldContainerStyle}>
                      <div
                        className="tw-flex tw-items-center tw-justify-between"
                        style={{ marginBottom: token.marginMD }}
                      >
                        <Text style={sectionTitleStyle}>{t('customer.sections.locations')}</Text>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          {isEditMode && (
                            <Checkbox
                              checked={includeInactiveLocations}
                              onChange={(e) => setIncludeInactiveLocations(e.target.checked)}
                            >
                              {t('customer.labels.includeInactiveLocations')}
                            </Checkbox>
                          )}
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              // Clear editing state for new location
                              setEditingLocation(null);
                              setEditingLocationFormData(null);
                              setEditingLocationId(null);
                              setLocationModalOpen(true);
                            }}
                            style={largeButtonStyle}
                          >
                            {t('customer.buttons.addLocation')}
                          </Button>
                        </div>
                      </div>
                      {locations.length === 0 ? (
                        <Alert
                          message={t('customer.empty.noLocations')}
                          description={t('customer.empty.noLocationsDesc')}
                          type="info"
                          showIcon
                        />
                      ) : (
                        <TableContainer>
                          <Table
                            dataSource={[...locations]
                              .filter((l) => includeInactiveLocations || l.isActive !== false)
                              .sort((a, b) => {
                                // Default location always comes first
                                if (a.isDefaultShipping && !b.isDefaultShipping) return -1;
                                if (!a.isDefaultShipping && b.isDefaultShipping) return 1;
                                return 0;
                              })}
                            rowKey="id"
                            rowClassName={(record) =>
                              record.isActive === false ? 'inactive-row' : ''
                            }
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            style={{ marginTop: token.marginMD }}
                            columns={[
                              {
                                title: t('customer.fields.defaultShipping'),
                                dataIndex: 'isDefaultShipping',
                                key: 'isDefaultShipping',
                                width: 140,
                                align: 'center',
                                render: (isDefault: boolean, record: CustomerLocation) => {
                                  const content = (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        minHeight: '40px',
                                      }}
                                    >
                                      {isDefault && (
                                        <CheckOutlined
                                          style={{
                                            color: token.colorPrimary,
                                            fontSize: token.sizeMD,
                                          }}
                                        />
                                      )}
                                    </div>
                                  );

                                  if (isEditMode && isNewItem(record.id)) {
                                    return (
                                      <Badge.Ribbon
                                        text={tCommon('new')}
                                        color={token.colorPrimary}
                                        placement="start"
                                      >
                                        <span>{content}</span>
                                      </Badge.Ribbon>
                                    );
                                  }
                                  return content;
                                },
                              },
                              {
                                title: t('customer.fields.defaultBilling'),
                                dataIndex: 'isDefaultBilling',
                                key: 'isDefaultBilling',
                                width: 130,
                                align: 'center',
                                render: (isDefault: boolean) => (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: '100%',
                                      minHeight: '40px',
                                    }}
                                  >
                                    {isDefault && (
                                      <CheckOutlined
                                        style={{
                                          color: token.colorPrimary,
                                          fontSize: token.sizeMD,
                                        }}
                                      />
                                    )}
                                  </div>
                                ),
                              },
                              {
                                title: t('customer.fields.locationName'),
                                dataIndex: 'locationName',
                                key: 'locationName',
                                width: 250,
                                ellipsis: true,
                                render: (text: string) => <Text strong>{text}</Text>,
                              },
                              {
                                title: t('customer.fields.address'),
                                key: 'address',
                                width: 300,
                                ellipsis: true,
                                render: (_: unknown, record: CustomerLocation) =>
                                  formatAddressForDisplay(record),
                              },
                              {
                                title: t('customer.fields.notes'),
                                dataIndex: 'accountNotes',
                                key: 'accountNotes',
                                width: 200,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('customer.fields.actions'),
                                key: 'actions',
                                width: 80,
                                fixed: 'right',
                                render: (_: unknown, record: CustomerLocation) => {
                                  const itemPerms = getItemPermissions(record);
                                  return (
                                    <Dropdown
                                      menu={{
                                        items: [
                                          {
                                            key: 'set-default-shipping',
                                            label: t('customer.actions.setAsDefaultShipping'),
                                            icon: <CheckCircleOutlined />,
                                            disabled: !itemPerms.canSetDefaultShipping,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleSetAsDefaultLocation(record.id);
                                            },
                                          },
                                          {
                                            key: 'set-default-billing',
                                            label: t('customer.actions.setAsDefaultBilling'),
                                            icon: <CheckCircleOutlined />,
                                            disabled: !itemPerms.canSetDefaultBilling,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleSetAsDefaultBillingLocation(record.id);
                                            },
                                          },
                                          {
                                            type: 'divider',
                                          },
                                          {
                                            key: 'edit',
                                            label: t('customer.actions.edit'),
                                            icon: <EditOutlined />,
                                            disabled: !itemPerms.canEdit,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleEditLocation(record);
                                            },
                                          },
                                          // In edit mode: existing items can be activated/deactivated, new items can be deleted
                                          // In create mode: all items can be deleted
                                          isEditMode && !isNewItem(record.id)
                                            ? {
                                                key: 'toggleStatus',
                                                label: itemPerms.isItemActive
                                                  ? t('customer.actions.deactivate')
                                                  : t('customer.actions.activate'),
                                                icon: itemPerms.isItemActive ? (
                                                  <CloseCircleOutlined />
                                                ) : (
                                                  <CheckCircleOutlined />
                                                ),
                                                ...getToggleStatusStyle(itemPerms, token),
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleToggleLocationStatus(record.id);
                                                },
                                              }
                                            : {
                                                key: 'delete',
                                                label: t('customer.actions.delete'),
                                                icon: <DeleteOutlined />,
                                                danger: true,
                                                disabled:
                                                  record.isDefaultShipping ||
                                                  record.isDefaultBilling,
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleDeleteLocation(record.id);
                                                },
                                              },
                                        ],
                                      }}
                                      trigger={['click']}
                                    >
                                      <Button
                                        type="text"
                                        icon={<MoreOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </Dropdown>
                                  );
                                },
                              },
                            ]}
                          />
                        </TableContainer>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Financial Information */}
                {currentStep === 1 && (
                  <div>
                    {/* Account Information Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.accountInformation')}
                      </Text>
                      {/* Row 1: Deposit + Cash Discount */}
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.deposit')}</Text>
                          <Controller
                            name="deposit"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                placeholder={t('customer.placeholders.deposit')}
                                suffix="%"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.cashDiscount')}</Text>
                          <Controller
                            name="cashDiscount"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                placeholder={t('customer.placeholders.cashDiscount')}
                                suffix="%"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>

                      {/* Row 3: Agreed Terms + Due In Days */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.agreedTerms')}
                          </Text>
                          <Controller
                            name="basePaymentTermId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.agreedTerms')}
                                allowClear
                                loading={isLoadingPaymentTerms}
                                options={paymentTerms.map((term) => ({
                                  label: i18n.language.startsWith('zh')
                                    ? term.termNameZh
                                    : term.termNameEn,
                                  value: term.paymentTermId,
                                }))}
                                onChange={(value) => {
                                  field.onChange(value);
                                  // Auto-fill due days when term is selected
                                  const selectedTerm = paymentTerms.find(
                                    (t) => t.paymentTermId === value
                                  );
                                  if (selectedTerm) {
                                    setValue('dueInDays', selectedTerm.dueDays);
                                  }
                                }}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.dueInDays')}
                          </Text>
                          <Controller
                            name="dueInDays"
                            control={control}
                            render={({ field }) => {
                              const selectedTermId = watch('basePaymentTermId');
                              const selectedTerm = paymentTerms.find(
                                (t) => t.paymentTermId === selectedTermId
                              );
                              const isZeroDayTerm = selectedTerm?.dueDays === 0;
                              return (
                                <Input
                                  {...field}
                                  size="large"
                                  type="number"
                                  placeholder={
                                    isZeroDayTerm
                                      ? t('customer.paymentTerms.notApplicable')
                                      : t('customer.placeholders.dueInDays')
                                  }
                                  suffix={t('customer.misc.suffix.days')}
                                  disabled={isZeroDayTerm}
                                  value={isZeroDayTerm ? '' : (field.value ?? '')}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      ? Number(e.target.value)
                                      : undefined;
                                    field.onChange(value);
                                  }}
                                  style={{
                                    height: `${controlHeightTokens.controlHeightXL}px`,
                                    borderRadius: `${radiusTokens.card}px`,
                                    width: '100%',
                                    backgroundColor: isZeroDayTerm
                                      ? token.colorBgContainerDisabled
                                      : token.colorBgContainer,
                                  }}
                                />
                              );
                            }}
                          />
                        </Col>
                      </Row>

                      {/* Row 4: Credit Limit + Hold */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.creditLimit')}</Text>
                          <Controller
                            name="creditLimit"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                placeholder={t('customer.placeholders.creditLimit')}
                                prefix="$"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.hold')}
                          </Text>
                          <Controller
                            name="hold"
                            control={control}
                            rules={{ required: t('customer.validation.holdRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.hold')}
                                  options={HOLD_STATUS_OPTIONS.map((option) => ({
                                    label: i18n.language.startsWith('zh')
                                      ? option.labelZh
                                      : option.label,
                                    value: option.value,
                                  }))}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.hold ? token.colorError : undefined,
                                  }}
                                />
                                {errors.hold && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.hold.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>

                      {/* Row 5: Primary Currency (F2.2 — replaces the hardcoded AUD) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('customer.fields.primaryCurrency')}
                          </Text>
                          <Controller
                            name="primaryCurrency"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder={t('customer.placeholders.currency')}
                                  showSearch
                                  loading={isLoadingCurrencies}
                                  options={currencyOptions}
                                  status={errors.primaryCurrency ? 'error' : undefined}
                                  style={formStyles.inputStyle}
                                />
                                {errors.primaryCurrency && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.primaryCurrency.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Tax Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.taxInfo')}
                      </Text>
                      {/* Row 1: ABN (Display only - from Step 1) */}
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('customer.fields.abn')}</Text>
                          <Controller
                            name="localTaxId"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                readOnly
                                style={{
                                  ...formStyles.inputStyle,
                                  backgroundColor: token.colorBgContainerDisabled,
                                  cursor: 'default',
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Tax Type + Tax Rate (display only) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.taxType')}</Text>
                          <Controller
                            name="taxTypeId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.taxType')}
                                allowClear
                                loading={isLoadingTaxTypes}
                                options={taxTypes.map((tax) => ({
                                  label: tax.taxType,
                                  value: tax.taxTypeId,
                                }))}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('customer.fields.taxRate')}</Text>
                          <Input
                            size="large"
                            readOnly
                            value={(() => {
                              const selectedTaxTypeId = watch('taxTypeId');
                              const selectedTax = taxTypes.find(
                                (t) => t.taxTypeId === selectedTaxTypeId
                              );
                              return selectedTax ? `${selectedTax.taxRate}%` : '';
                            })()}
                            placeholder="-"
                            style={{
                              ...formStyles.inputStyle,
                              backgroundColor: token.colorBgContainerDisabled,
                              cursor: 'default',
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                {/* Step 3: Preference */}
                {currentStep === 2 && (
                  <div>
                    {/* Billing Preference Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.billingPreference')}
                      </Text>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('customer.fields.invoicee')}</Text>
                          <Controller
                            name="invoiceeId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                onChange={(value) => field.onChange(value ?? null)}
                                size="large"
                                placeholder={t('customer.placeholders.invoicee')}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label ?? '')
                                    .toString()
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={contacts
                                  .filter((contact) => contact.isActive !== false)
                                  .map((contact) => ({
                                    label: contact.name,
                                    value: parseInt(contact.id.replace(/\D/g, '')) || 0,
                                  }))}
                                style={{
                                  ...formStyles.inputStyle,
                                  width: '100%',
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Shipping & Delivery Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('customer.sections.shippingDelivery')}
                      </Text>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('customer.fields.shippingInstruction')}</Text>
                          <Controller
                            name="shippingInstruction"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.shippingInstruction')}
                                rows={4}
                                style={{
                                  ...formStyles.inputStyle,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            {t('customer.fields.defaultShippingMarkFront')}
                          </Text>
                          <Controller
                            name="defaultShippingMarkFront"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.shippingMarkFront')}
                                style={{
                                  ...formStyles.inputStyle,
                                  height: '128px',
                                }}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            {t('customer.fields.defaultShippingMarkSide')}
                          </Text>
                          <Controller
                            name="defaultShippingMarkSide"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('customer.placeholders.shippingMarkSide')}
                                style={{
                                  ...formStyles.inputStyle,
                                  height: '128px',
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}
              </Form>
            </div>
          </div>

          {/* Sticky Bottom Buttons */}
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 300, // Sider width
              right: 0,
              height: `${FOOTER_HEIGHT}px`,
              backgroundColor: token.colorBgContainer,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              padding: `${token.paddingMD}px ${token.paddingLG}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 99,
            }}
          >
            {/* Left: Exit button (always visible) */}
            <Button danger size="large" onClick={handleBack} style={largeButtonStyle}>
              {t('customer.buttons.exit')}
            </Button>

            {/* Right: Save/Previous/Next/Submit buttons */}
            <div className="tw-flex tw-gap-3">
              {/* Previous button (hidden on step 1) */}
              {currentStep > 0 && (
                <Button
                  size="large"
                  onClick={handlePrevious}
                  style={largeButtonStyle}
                  disabled={isSaving}
                >
                  {t('customer.actions.previous')}
                </Button>
              )}

              {/* Next button (hidden on last step) */}
              {currentStep < stepItems.length - 1 && (
                <Button
                  size="large"
                  onClick={handleNext}
                  style={largeButtonStyle}
                  disabled={isSaving}
                >
                  {t('customer.actions.next')}
                </Button>
              )}

              {/* Save button - Edit mode: shown on ALL steps with cumulative save
                  Create mode: only on last step as "Create Vendor" */}
              {isEditMode ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={saveCurrentStep}
                  loading={isSaving}
                  style={largeButtonStyle}
                >
                  {t('customer.actions.save')}
                </Button>
              ) : (
                currentStep === stepItems.length - 1 && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit(onSubmit)}
                    style={largeButtonStyle}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    {t('customer.buttons.createCustomer')}
                  </Button>
                )
              )}
            </div>
          </div>
        </Content>
      </Layout>

      {/* Location Modal */}
      <LocationFormModal
        visible={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setEditingLocation(null);
          setEditingLocationFormData(null);
          setEditingLocationId(null);
        }}
        onSuccess={handleAddLocation}
        location={
          editingLocationId
            ? locationDetailLoading
              ? null
              : locationDetailFormData
            : editingLocationFormData
        }
      />

      {/* Company Found Confirmation Modal */}
      <Modal
        open={validationMachine.showModal}
        onCancel={handleCancelCompanyModal}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <WarningOutlined style={{ color: token.colorWarning, fontSize: '20px' }} />
            <span>{t('customer.modals.companyFoundTitle')}</span>
          </div>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button
              onClick={handleCancelCompanyModal}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('customer.actions.cancel')}
            </Button>
            <Button
              type="primary"
              loading={isLoadingCompanyData}
              onClick={handleLoadCompanyData}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('customer.actions.loadData')}
            </Button>
          </div>
        }
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <Typography.Paragraph>
            {t('customer.modals.companyFoundDescription')}
          </Typography.Paragraph>
          {validationMachine.localTaxId && (
            <div
              style={{
                backgroundColor: token.colorFillQuaternary,
                borderRadius: radiusTokens.panel,
                padding: '16px',
                marginTop: '12px',
              }}
            >
              <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {t('customer.fields.taxIdAbn')}:{' '}
                <span style={{ fontFamily: 'monospace' }}>{validationMachine.localTaxId}</span>
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('customer.modals.companyExistsNotCustomer')}
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CustomerFormWizardContent;
